import { useEffect } from 'react';
// Themes (#251, #302, #309). One list decides the whole look: the default colors (matching the device's light or dark
// setting, or always light, or always dark), each color theme in a light and a dark version, and gradient themes with
// their own fixed look. A color theme sets a main color and a font, and styles.css mixes the surfaces from it; a gradient
// theme sets a background gradient, glass cards and an accent gradient (styles.css, [data-gradient]). Fonts are bundled
// and load only when picked. Ids must match the server's THEMES.

export type Mode = 'system' | 'light' | 'dark';

/** A gradient theme's look; `dark` themes have white text, light ones dark text. */
export interface Gradient {
  dark: boolean;
  /** The page background. */
  bg: string;
  /** Primary buttons and the selected student tab. */
  accent: string;
  /** A solid color for bars, close to the gradient's start. */
  base: string;
  /** Solid surface for pop-up menus and sheets. */
  surface: string;
  /** Links, the selected tab and other highlights. */
  primary: string;
  text: string;
  muted: string;
}

export interface Palette {
  id: string;
  name: string;
  emoji: string;
  /** Main color in light mode (white text must read well on it). */
  light: string;
  /** Main color in dark mode (dark text reads on it). */
  dark: string;
  font: string;
  loadFont: () => Promise<unknown>;
  /** Light, dark, or following the device. Gradient themes have their own look. */
  mode: Mode;
  /** The color theme this is a version of ('' for the default colors); unset for gradient themes. */
  base?: string;
  gradient?: Gradient;
  /** The swatch on the theme card. */
  swatch: string;
}

const SYSTEM_FONT = "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

const fonts = {
  nunito: { font: `'Nunito', ${SYSTEM_FONT}`, load: () => Promise.all([import('@fontsource/nunito/400.css'), import('@fontsource/nunito/700.css')]) },
  fredoka: { font: `'Fredoka', ${SYSTEM_FONT}`, load: () => Promise.all([import('@fontsource/fredoka/400.css'), import('@fontsource/fredoka/600.css')]) },
  quicksand: { font: `'Quicksand', ${SYSTEM_FONT}`, load: () => Promise.all([import('@fontsource/quicksand/400.css'), import('@fontsource/quicksand/700.css')]) },
  baloo: { font: `'Baloo 2', ${SYSTEM_FONT}`, load: () => Promise.all([import('@fontsource/baloo-2/400.css'), import('@fontsource/baloo-2/700.css')]) },
  lexend: { font: `'Lexend', ${SYSTEM_FONT}`, load: () => Promise.all([import('@fontsource/lexend/400.css'), import('@fontsource/lexend/700.css')]) },
  comic: { font: `'Comic Neue', ${SYSTEM_FONT}`, load: () => Promise.all([import('@fontsource/comic-neue/400.css'), import('@fontsource/comic-neue/700.css')]) },
  atkinson: {
    font: `'Atkinson Hyperlegible', ${SYSTEM_FONT}`,
    load: () => Promise.all([import('@fontsource/atkinson-hyperlegible/400.css'), import('@fontsource/atkinson-hyperlegible/700.css')]),
  },
  patrick: { font: `'Patrick Hand', ${SYSTEM_FONT}`, load: () => import('@fontsource/patrick-hand/400.css') },
  varela: { font: `'Varela Round', ${SYSTEM_FONT}`, load: () => import('@fontsource/varela-round/400.css') },
  system: { font: SYSTEM_FONT, load: async () => undefined },
};

/** The color themes, each offered in a light and a dark version. */
const COLORS: { id: string; name: string; emoji: string; light: string; dark: string; font: keyof typeof fonts }[] = [
  { id: 'ocean', name: 'Ocean Breeze', emoji: '🌊', light: '#0e7490', dark: '#22d3ee', font: 'nunito' },
  { id: 'sunshine', name: 'Sunshine', emoji: '☀️', light: '#c2410c', dark: '#fb923c', font: 'fredoka' },
  { id: 'forest', name: 'Forest Friends', emoji: '🌲', light: '#15803d', dark: '#4ade80', font: 'quicksand' },
  { id: 'bubblegum', name: 'Bubblegum', emoji: '🍬', light: '#be185d', dark: '#f472b6', font: 'baloo' },
  { id: 'galaxy', name: 'Galaxy', emoji: '🪐', light: '#6d28d9', dark: '#a78bfa', font: 'lexend' },
  { id: 'berry', name: 'Berry Blast', emoji: '🫐', light: '#a21caf', dark: '#e879f9', font: 'comic' },
  { id: 'sky', name: 'Sky High', emoji: '☁️', light: '#0369a1', dark: '#38bdf8', font: 'atkinson' },
  { id: 'citrus', name: 'Citrus Splash', emoji: '🍋', light: '#4d7c0f', dark: '#a3e635', font: 'patrick' },
  { id: 'sunset', name: 'Sunset', emoji: '🌅', light: '#e11d48', dark: '#fb7185', font: 'varela' },
];

const g = (...stops: string[]) => `linear-gradient(135deg, ${stops.join(', ')})`;

/** Gradient themes (#302, #309): bold dark ones like the School Resources popup, and soft light ones. */
const GRADIENTS: { id: string; name: string; emoji: string; font: keyof typeof fonts; gradient: Gradient }[] = [
  {
    id: 'aurora',
    name: 'Aurora Glow',
    emoji: '✨',
    font: 'nunito',
    gradient: { dark: true, bg: g('#1e1b4b 0%', '#2e1065 55%', '#4a044e 100%'), accent: g('#6366f1', '#8b5cf6 50%', '#ec4899'), base: '#1e1b4b', surface: '#2a2163', primary: '#a78bfa', text: '#ffffff', muted: '#c7d2fe' },
  },
  {
    id: 'ocean-depths',
    name: 'Ocean Depths',
    emoji: '🐋',
    font: 'quicksand',
    gradient: { dark: true, bg: g('#0b1a33 0%', '#0c3550 55%', '#0d5560 100%'), accent: g('#0284c7', '#0891b2 50%', '#0d9488'), base: '#0b1a33', surface: '#13304a', primary: '#5eead4', text: '#ffffff', muted: '#bae6fd' },
  },
  {
    // Light and airy in the same teal and blue as Ocean Depths (#698): the home page's and the server's default look.
    id: 'ocean-mist',
    name: 'Ocean Mist',
    emoji: '🫧',
    font: 'quicksand',
    // A soft teal-to-blue wash on bright white (#767): a true teal (less green) flowing into a clear blue.
    gradient: { dark: false, bg: g('#f1f9fa 0%', '#f8f9fc 45%', '#f0f4fd 100%'), accent: g('#0e8fa0', '#1e40af'), base: '#f2f8fb', surface: '#ffffff', primary: '#0b7a8a', text: '#10283b', muted: '#4f6170' },
  },
  {
    id: 'northern-lights',
    name: 'Northern Lights',
    emoji: '🌌',
    font: 'lexend',
    gradient: { dark: true, bg: g('#04201f 0%', '#0b3b3a 45%', '#1e1b4b 100%'), accent: g('#059669', '#0891b2 50%', '#6366f1'), base: '#04201f', surface: '#123a3c', primary: '#6ee7b7', text: '#ffffff', muted: '#a7f3d0' },
  },
  {
    id: 'ember',
    name: 'Ember Glow',
    emoji: '🔥',
    font: 'fredoka',
    gradient: { dark: true, bg: g('#1f0a0a 0%', '#431407 50%', '#4c0519 100%'), accent: g('#ea580c', '#e11d48 60%', '#be185d'), base: '#1f0a0a', surface: '#3a1712', primary: '#fdba74', text: '#ffffff', muted: '#fecaca' },
  },
  {
    id: 'cotton-candy',
    name: 'Cotton Candy',
    emoji: '🍭',
    font: 'baloo',
    gradient: { dark: false, bg: g('#fce7f3 0%', '#f3e8ff 50%', '#dbeafe 100%'), accent: g('#ec4899', '#a855f7 50%', '#3b82f6'), base: '#f8f5ff', surface: '#ffffff', primary: '#9333ea', text: '#2e1a47', muted: '#6b5b7b' },
  },
  {
    id: 'peach',
    name: 'Peach Sunrise',
    emoji: '🍑',
    font: 'varela',
    gradient: { dark: false, bg: g('#fff7ed 0%', '#ffedd5 45%', '#fce7f3 100%'), accent: g('#f97316', '#ec4899'), base: '#fff4ea', surface: '#ffffff', primary: '#c2410c', text: '#3b2416', muted: '#7c5a4a' },
  },
  {
    id: 'mint',
    name: 'Mint Breeze',
    emoji: '🌿',
    font: 'quicksand',
    gradient: { dark: false, bg: g('#d1fae5 0%', '#ecfeff 50%', '#e0f2fe 100%'), accent: g('#059669', '#0284c7'), base: '#effbf6', surface: '#ffffff', primary: '#047857', text: '#10302a', muted: '#4b6b63' },
  },
  {
    id: 'lavender',
    name: 'Lavender Mist',
    emoji: '💜',
    font: 'nunito',
    gradient: { dark: false, bg: g('#ede9fe 0%', '#f5f3ff 50%', '#fae8ff 100%'), accent: g('#7c3aed', '#c026d3'), base: '#f4f1ff', surface: '#ffffff', primary: '#6d28d9', text: '#2a2145', muted: '#625a7a' },
  },
];

const DEFAULT = { light: '#4f46e5', dark: '#8b85ff' };

/**
 * The 8 child colors (#749). A child keeps one of these; each theme draws it in its own colors (#753, #757): the
 * theme's main color plus accents that go with it, at a soft strength, so the picker, avatars, cards and the
 * child's app look like part of the theme.
 */
export const KID_COLORS = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#8b5cf6', '#14b8a6'];
/**
 * Hue turns from the theme's main color (#757): the main color, its opposite, a near neighbour, a split complement, the
 * other neighbour, the other split, then the two quarter turns. The theme's own color plus accents that go with it,
 * rather than eight of the same hue.
 */
const KID_HUES = [0, 180, 40, -140, -40, 140, 90, -90];

function hexHsl(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l * 100];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h * 60, s * 100, l * 100];
}

function hslHex(h: number, s: number, l: number): string {
  const hue = (((h % 360) + 360) % 360) / 360;
  const [S, L] = [s / 100, l / 100];
  const q = L < 0.5 ? L * (1 + S) : L + S - L * S;
  const p = 2 * L - q;
  const ch = (t: number) => {
    t = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
    const v = t < 1 / 6 ? p + (q - p) * 6 * t : t < 1 / 2 ? q : t < 2 / 3 ? p + (q - p) * (2 / 3 - t) * 6 : p;
    return Math.round(v * 255).toString(16).padStart(2, '0');
  };
  return `#${ch(hue + 1 / 3)}${ch(hue)}${ch(hue - 1 / 3)}`;
}

/** A theme's 8 child colors, built from its main color; null for "Match device", which uses them as they are. */
export function kidShades(p: Palette): string[] | null {
  if (p.mode === 'system') return null;
  const dark = p.gradient ? p.gradient.dark : p.mode === 'dark';
  const main = p.gradient ? p.gradient.primary : dark ? p.dark : p.light;
  const [h, s] = hexHsl(main);
  const sat = Math.min(Math.max(s * 0.6, 34), dark ? 55 : 48);
  return KID_HUES.map((o) => {
    const hue = (((h + o) % 360) + 360) % 360;
    // Red reads as a warning and clashes with most themes (#759): a soft slate in the theme's own hue instead.
    if (hue >= 340 || hue <= 20) return hslHex(h, dark ? 22 : 20, dark ? 70 : 48);
    // Yellows and oranges get a little lift so they don't turn muddy.
    const warm = hue >= 35 && hue <= 100;
    return hslHex(hue, warm ? Math.min(sat + 10, 64) : sat, dark ? (warm ? 74 : 72) : warm ? 56 : 50);
  });
}

/** Dark initials on a light shade, white on a deep one. */
export function kidInkFor(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  return (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255 > 0.62 ? '#111827' : '#ffffff';
}

const kidVar = (hex: string) => `--kc-${hex.replace('#', '').toLowerCase()}`;
/** A child's color in the current theme (#749); any other color stays as it is. */
export const kidColor = (hex: string) => (/^#[0-9a-f]{6}$/i.test(hex) ? `var(${kidVar(hex)}, ${hex})` : hex);
/** The initials color on a child's color in the current theme. */
export const kidInk = (hex: string) => (/^#[0-9a-f]{6}$/i.test(hex) ? `var(${kidVar(hex)}-ink, ${kidInkFor(hex)})` : '#ffffff');

/** Every theme, in the order they're offered. */
export const PALETTES: Palette[] = [
  // 'system' is picking Match device; '' (never picked) also lands here when the server's default is Match device (#360).
  { id: 'system', name: 'Default · Match device', emoji: '🛡️', mode: 'system', base: '', ...DEFAULT, font: SYSTEM_FONT, loadFont: fonts.system.load, swatch: g('#ffffff 0%', '#ffffff 49%', '#0b1020 51%') },
  { id: 'default-light', name: 'Default Light', emoji: '☀️', mode: 'light', base: '', ...DEFAULT, font: SYSTEM_FONT, loadFont: fonts.system.load, swatch: g('#eef0f7', '#ffffff') },
  { id: 'default-dark', name: 'Default Dark', emoji: '🌙', mode: 'dark', base: '', ...DEFAULT, font: SYSTEM_FONT, loadFont: fonts.system.load, swatch: g('#0b1020', '#1c2440') },
  ...COLORS.flatMap((c): Palette[] => [
    { id: c.id, name: c.name, emoji: c.emoji, mode: 'light', base: c.id, light: c.light, dark: c.dark, font: fonts[c.font].font, loadFont: fonts[c.font].load, swatch: g(c.light, c.dark) },
    {
      id: `${c.id}-dark`,
      name: `${c.name} Dark`,
      emoji: c.emoji,
      mode: 'dark',
      base: c.id,
      light: c.light,
      dark: c.dark,
      font: fonts[c.font].font,
      loadFont: fonts[c.font].load,
      swatch: g('#0b1020 0%', `${c.dark} 100%`),
    },
  ]),
  ...GRADIENTS.map(
    (t): Palette => ({
      id: t.id,
      name: t.name,
      emoji: t.emoji,
      mode: t.gradient.dark ? 'dark' : 'light',
      gradient: t.gradient,
      light: t.gradient.primary,
      dark: t.gradient.primary,
      font: fonts[t.font].font,
      loadFont: fonts[t.font].load,
      swatch: t.gradient.accent,
    }),
  ),
];

export const paletteById = (id: string | null | undefined) => PALETTES.find((p) => p.id === (id ?? '')) ?? PALETTES[0]!;

/** Who's using this browser tab: the Student Portal keeps its own theme separate from the parent's. */
const cacheKey = () => (location.pathname.startsWith('/student') ? 'cg.palette.student' : 'cg.palette.parent');

const GRADIENT_VARS = ['bg', 'accent', 'base', 'surface', 'primary', 'text', 'muted'] as const;

/** Apply a theme now (colors right away, the font as soon as it loads) and remember it for the next page load. */
export function applyPalette(id: string | null | undefined, remember = true) {
  const p = paletteById(id);
  const root = document.documentElement;
  const set = (name: string, value: string | null) => (value === null ? root.style.removeProperty(name) : root.style.setProperty(name, value));

  // Light or dark: the theme decides (the default "Match device" follows the device).
  if (p.mode === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', p.mode);

  if (p.base === '') {
    root.removeAttribute('data-palette');
    set('--pal-light', null);
    set('--pal-dark', null);
    set('--pal-font', null);
  } else {
    root.setAttribute('data-palette', p.base ?? p.id);
    set('--pal-light', p.light);
    set('--pal-dark', p.dark);
    set('--pal-font', p.font);
    void p.loadFont().catch(() => undefined);
  }

  if (p.gradient) {
    root.setAttribute('data-gradient', p.gradient.dark ? 'dark' : 'light');
    for (const k of GRADIENT_VARS) set(`--g-${k}`, p.gradient[k]);
  } else {
    root.removeAttribute('data-gradient');
    for (const k of GRADIENT_VARS) set(`--g-${k}`, null);
  }

  // The child colors in this theme's shades (#749).
  const shades = kidShades(p);
  KID_COLORS.forEach((c, i) => {
    const shade = shades?.[i] ?? null;
    set(kidVar(c), shade);
    set(`${kidVar(c)}-ink`, shade && kidInkFor(shade));
  });

  if (!remember) return;
  try {
    if (p.id) localStorage.setItem(cacheKey(), p.id);
    else localStorage.removeItem(cacheKey());
  } catch {
    /* storage unavailable: applies for this page load only */
  }
}

/**
 * Show a theme on one page without saving it (#352: the public home page is always blue and teal). Returns a function
 * that puts the visitor's own remembered theme back.
 */
/** The public site's look (#698): the home page, sign-in and the other pages people see before signing in. */
export const SITE_THEME = 'ocean-mist';

/** Show the public site's look while a public page is open; the parent's own theme comes back after. */
export const useSiteTheme = () => useEffect(() => previewPalette(SITE_THEME), []);

export function previewPalette(id: string): () => void {
  applyPalette(id, false);
  return () => {
    let cached: string | null = null;
    try {
      cached = localStorage.getItem(cacheKey());
    } catch {
      /* storage unavailable */
    }
    applyPalette(cached, false);
  };
}

/** Call once at startup so the last theme shows before the account loads (no flash of the default look). */
export function initPalette() {
  try {
    const cached = localStorage.getItem(cacheKey());
    if (cached) applyPalette(cached);
  } catch {
    /* storage unavailable */
  }
}

/**
 * Light or dark used to be a separate, per-browser switch (cg.theme). Turn an old choice into the matching default
 * theme once, so nobody's look changes (#309). Returns the theme to save, or null if there's nothing to move.
 */
export function legacyModeTheme(current: string): string | null {
  try {
    const old = localStorage.getItem('cg.theme');
    if (!old) return null;
    localStorage.removeItem('cg.theme');
    if (current) return null; // they already picked a theme; it now decides light or dark
    return old === 'dark' ? 'default-dark' : old === 'light' ? 'default-light' : null;
  } catch {
    return null;
  }
}
